import { t, te } from "@/language/translation";
/* eslint-disable no-console */
import axios from "axios";
import loginstore from "@/store";
import definition from "./apiDefinition";
import { transactionId } from "@/sentry";
import { isProduction } from "@/functions/environment";
import { ensureFreshAccessToken, recoverFromSessionError } from "@/keycloak/AuthenticationSession";

const baseURL = `${window.location.protocol}//${window.location.host}/api/`;

const apiWrapper = axios.create({
  baseURL,
});

export const currentVersion = (import.meta.env.__VERSION__ ?? "").replace(/(\r\n|\n|\r)/gm, "");

const api: Record<string, any> = {};

// list all api routes
// console.log(
//   Object.entries(definition).flatMap(([name, component]) =>
//     Object.entries(component.endpoints).map(
//       ([key, value]) =>
//         `${(value.method as string).toLowerCase()}: /api/${component.path}/${value.route === "/" ? "" : value.route}`,
//     ),
//   ),
// );

Object.entries(definition).forEach(([name, component]) => {
  const endpoints = Object.entries(component.endpoints).reduce(
    (apiObject, [endpointName, endpointConfig]: [string, any]) => {
      apiObject[endpointName] = async (...args: any[]) => {
        let id;
        let payload;
        let routeParams;
        if (typeof args[0] === "object") {
          [payload, routeParams] = args;
        } else {
          [id, payload, routeParams] = args;
        }
        if (endpointConfig.route.includes(":id") && id == null) {
          console.warn(`${name}.${endpointName} contains id but no id provided.`);
        }
        let url = `${component.path}/${endpointConfig.route.replace(":id", id)}`;
        Object.entries(routeParams || {}).forEach(([key, values]) => {
          url = url.replace(`:${key}`, values as any);
        });
        let authenticationRetried = false;
        while (true) {
          try {
            const accessToken = await ensureFreshAccessToken();
            const options: any = {
              method: endpointConfig.method,
              url,
              data: payload ?? {},
              withCredentials: true,
              headers: {
                "X-Version": !isProduction() ? "1.1.0" : currentVersion,
                "X-Transaction-ID": transactionId(),
                Authorization: `Bearer ${accessToken}`,
              },
            };
            if (endpointConfig.forceBlob === true) {
              options.responseType = "blob";
            }

            const response = await apiWrapper(options);
            if (response?.data?.message) {
              window.$toast?.({
                severity: "success",
                detail: (te(`api.toast.success.${response.data.message}`)
                  ? t(`api.toast.success.${response.data.message}`)
                  : t("api.toast.success.common")) as string,
                summary: t("api.toast.success.title") as string,
              });
            }
            return response.data;
          } catch (error: any) {
            if (error?.response?.data?.message === "VALID_LOGIN_NO_LISA_USER") {
              loginstore.dispatch("login/setSetup", {
                state: true,
              });
              return null;
            }
            if (error.message === "Network Error") {
              window.$toast?.({
                severity: "error",
                summary: t("api.toast.error.title"),
                detail: t("api.toast.error.networkError") as string,
              });
              return null;
            }

            if (axios.isAxiosError(error)) {
              const isSessionError =
                error.response != null &&
                (error.response.status === 401 || error.response.status === 403) &&
                (error.response.data?.message === "missingSession" ||
                  error.response.data?.message === "expiredSession" ||
                  error.response.data?.message === "invalidSession");

              if (isSessionError && !authenticationRetried) {
                authenticationRetried = true;
                await recoverFromSessionError();
                continue;
              }

              if (isSessionError) {
                window.$toast?.({
                  severity: "error",
                  detail: t("api.toast.error.expiredSession") as string,
                  summary: t("api.toast.error.title"),
                });
                return null;
              }

              console.error("Axios request failed:", error);
              if (error.response?.data?.message && endpointConfig.ignoreError !== true) {
                let errorMessage;
                if (te(`api.toast.error.${error.response.data.message}`)) {
                  errorMessage = [t(`api.toast.error.${error.response.data.message}`)];
                } else {
                  errorMessage = [t("api.toast.error.common")];
                }

                if (Array.isArray(error?.response?.data?.errors) && error.response.status === 400) {
                  const visible = error?.response?.data?.errors.filter((el: any) => {
                    if (el.location === "body") {
                      return te(`api.toast.error.validator.${el.param}`);
                    }
                    return false;
                  }); // collecting all errors that didn't pass validator!
                  errorMessage = [
                    ...errorMessage,
                    ...visible.map(
                      (el: any, index: number) =>
                        `${index + 1}. ${t(`api.toast.error.validator.${el.param}`)}`,
                    ),
                  ];
                }
                const message = errorMessage;

                window.$toast?.({
                  severity: "error",
                  summary: t("api.toast.error.title") as string,
                  detail: message[0],
                });
              } else if (endpointConfig.ignoreError !== true) {
                window.$toast?.({
                  severity: "error",
                  detail: t("api.toast.error.common") as string,
                  summary: t("api.toast.error.title") as string,
                });
              }

              // If there is a version mismatch, don't throw the error.
              if (error.response?.status === 409 && error.response.data === "reloadRequired") {
                return null;
              }
            }
            throw error;
          }
        }
      };
      return apiObject;
    },
    {} as Record<string, any>,
  );
  api[name] = endpoints;
});

export default api;

export const ApiDefinition = Object.freeze(definition);
